<template>
  <div v-if="isAutomap" class="map-automap-title">
    <span>
      수집시각: <span>{{ automapLabel('time') }}</span>
    </span>
    <el-tooltip content="수집 즉시수행" placement="top" v-if="!isEditMode && isSettingAuth">
      <span class="execute-automap-btn">
        <i class="fas fa-play-circle text-14 c-pointer" @click="onExecuteScheduleClick"></i>
      </span>
    </el-tooltip>
    <span>
      수집범위: <span>{{ automapLabel('ip') }}</span>
    </span>
    <el-tooltip placement="bottom" v-if="automapLabel('ipCount')">
      <span class="ip-count">{{ automapLabel('ipCount') }}</span>
      <div v-html="getTooltipIpRange()" slot="content"></div>
    </el-tooltip>
    <el-tooltip content="수집 설정" placement="top" v-if="!isEditMode && isSettingAuth">
      <span class="execute-automap-btn">
        <i class="fas fa-cog text-14 c-pointer" @click="onEditScheduleSettingClick"></i>
      </span>
    </el-tooltip>
    <el-tooltip content="신규장비 강조" placement="top" v-if="!isEditMode">
      <span :class="['map-automap-title-clickable', (isDeviceChanged('add') ? 'add' : ''), (highlightType === 'add' ? 'selected' : '')]" @click="onDeviceHighlightClick('add')">
        신규장비: <span>{{ automapLabel('add') }}</span>
      </span>
    </el-tooltip>
    <el-tooltip content="삭제장비 강조" placement="top" v-if="!isEditMode">
      <span :class="['map-automap-title-clickable', (isDeviceChanged('delete') ? 'delete' : ''), (highlightType === 'delete' ? 'selected' : '')]" @click="onDeviceHighlightClick('delete')">
        삭제장비: <span>{{ automapLabel('delete') }}</span>
      </span>
    </el-tooltip>
    <span class="more" @click="onMoreClick" v-if="!isEditMode">
      더보기 >
    </span>
    <w-automap-schedule-setting-popup ref="schedulePopup" :popupType="popupType" :configSchedule="configScheduleData" :showTarget="false" :scheduleTypeId="'AUTO_TOPOLOGY'" @loadData="onChangeScheduleSetting"/>
  </div>
</template>

<script>
import { forEach, isNil, isEqual, isNumber } from 'lodash';
import { getDateToString } from '@/utils/date';
import MapAPI from '@/api/topologymap/common';
import MapConst from '@/constants/topologymap';
import Base from '@/mixins/topologymap/base';
import msgUtil from '@/utils/message';
import { MapInfo } from '@/views/topologymap/js/maps';

import WAutomapScheduleSettingPopup from '@/views/topologymap/components/popup/AutomapScheduleSettingPopup';

export default {
  name: 'WMapAutomapTitle',
  mixins: [Base],
  components: {
    WAutomapScheduleSettingPopup,
  },
  data() {
    return {
      highlightType: null,
      isProcess: false,
      popupType: '',
      configScheduleData: {},
      webSocket: null,
    };
  },
  watch: {
    /**
     * 맵을 이동하면 호출된다.
     * @author KangBongHun(toto1323)
     * @return {[type]} [description]
     */
    currentMapId() {
      this.init();
      this.checkAutomapProgress();
    },
  },
  computed: {
    getAutomapInfo() {
      return this.automapInfo || {};
    },
    getSchedule() {
      return this.getAutomapInfo.schedule || {};
    },
    getIpRanges() {
      return this.getAutomapInfo.ipRanges || [];
    },
    getDeviceStatus() {
      return this.getAutomapInfo.deviceStatus || {};
    },
    /**
     * 자동맵 정보 라벨 반환
     * @author KangBongHun(toto1323)
     * @return {[type]} [description]
     */
    automapLabel() {
      return (type) => {
        let label = '';
        switch (type) {
          case 'time':
            label = this.getSchedule.lastScheduleDatetime ? getDateToString(this.getSchedule.lastScheduleDatetime) : '수집 된 이력이 없습니다.';
            break;
          case 'ip': {
            const ip = this.getIpRanges[0];
            label = ip ? this.getIpRange(ip) : '설정 된 구간이 없습니다.';
            break;
          }
          case 'ipCount': {
            const ipCount = this.getIpRanges.length;
            label = ipCount > 1 ? `+${ipCount - 1}` : null;
            break;
          }
          case 'add':
            label = this.getDeviceStatus.newCnt;
            break;
          case 'delete':
            label = this.getDeviceStatus.deleteCnt;
            break;
          default:
        }
        return label;
      };
    },
    /**
     * 장비 변경 타입 별 변경 여부 반환
     * @author KangBongHun(toto1323)
     * @return {Boolean} [description]
     */
    isDeviceChanged() {
      return (type) => {
        let changed = false;
        switch (type) {
          case 'add':
            changed = this.getDeviceStatus.newCnt > 0;
            break;
          case 'delete':
            changed = this.getDeviceStatus.deleteCnt > 0;
            break;
          default:
        }
        return changed;
      };
    },
  },
  created() {
    // 추가,삭제 장비 강조 요청 이벤트
    this.$mapEventBus.$on(MapConst.MAP_EVENTBUS.AUTOMAP_DEVICE_HIGHLIGHT, this.onDeviceHighlight);
  },
  mounted() {
    this.openWebSocket();
  },
  beforeDestroy() {
    this.closeWebSocket();
    this.init();
    this.$mapEventBus.$off(MapConst.MAP_EVENTBUS.AUTOMAP_DEVICE_HIGHLIGHT, this.onDeviceHighlight);
  },
  methods: {
    /**
     * 초기화
     * @author KangBongHun(toto1323)
     * @return {[type]} [description]
     */
    init() {
      this.isProcess = false;
      MapInfo.getInstance().clearAutomapProcess();
    },
    /**
     * 연결정보 수집 스케줄을 즉시 수행한다.
     * @author KangBongHun(toto1323)
     * @return {[type]} [description]
     */
    onExecuteScheduleClick() {
      if (this.isProcess) {
        this.$message.error('연결정보 수집이 이미 수행중입니다.');
        return;
      }
      const scheduleId = this.getSchedule.scheduleId;
      MapAPI.executeAutomapSchedule(scheduleId).then((response) => {
        if (response.data.success) {
          this.$message(msgUtil.getResultMsgData('시작', '연결정보 수집', true));
          this.showAutotopologyModelingProgress(scheduleId);
          this.isProcess = true;
        } else {
          this.$message(msgUtil.getResultMsgData('시작', '연결정보 수집', false));
          this.isProcess = false;
        }
      });
    },
    /**
     * 변경 장비 강조 버튼 클릭
     * @author KangBongHun(toto1323)
     * @param  {[type]} type [description]
     * @return {[type]}      [description]
     */
    onDeviceHighlightClick(type) {
      if (this.highlightType === type) {
        this.highlightType = null;
      } else {
        this.highlightType = type;
      }
      this.highlightElements();
    },
    /**
     * 연결정보 수집 설정 버튼 클릭
     * @author KangBongHun(toto1323)
     * @return {[type]} [description]
     */
    onEditScheduleSettingClick() {
      const scheduleId = this.getSchedule.scheduleId;
      MapAPI.getAutoTopologyScheduleInfo(scheduleId).then((response) => {
        const result = response.data;

        if (result.success) {
          this.popupType = 'edit';
          this.configScheduleData = result.data;
          this.$refs.schedulePopup.open();
        }
      });
    },
    /**
     * 더보기 클릭
     * 연결장비 정보 화면을 나타낸다.
     * @author KangBongHun(toto1323)
     * @return {[type]} [description]
     */
    onMoreClick() {
      this.$mapEventBus.$emit(MapConst.MAP_EVENTBUS.AUTOMAP_DEVICE_VISIBLE, true);
    },
    /**
     * 연결정보 수집 진행 상태 프로그래스 표현
     * @author KangBongHun(toto1323)
     * @param  {[type]} scheduleId [description]
     * @param  {[type]} mapId      [description]
     * @return {[type]}            [description]
     */
    showAutotopologyModelingProgress(scheduleId) {
      this.$store.dispatch('autotopologyModelingTask', scheduleId);
      this.isProcess = true;
    },
    /**
     * 강조 할 변경장비 타입 변경
     * @author KangBongHun(toto1323)
     * @param  {[type]} type [description]
     * @return {[type]}      [description]
     */
    onDeviceHighlight(type) {
      this.highlightType = type;
    },
    /**
     * 장비 강조
     * @author KangBongHun(toto1323)
     * @return {[type]} [description]
     */
    highlightElements() {
      const graph = MapInfo.getInstance(this).getGraph();

      if (this.highlightType) {
        const elements = [];
        graph.graphModel.forEachNode((element) => {
          if (element.legend) {
            if ((this.highlightType === 'add' && element.legend.status === 'N') || (this.highlightType === 'delete' && element.legend.status === 'D')) {
              elements.push(element);
            }
          }
        });
        MapInfo.getInstance(this).highlightElements(elements);
      } else {
        MapInfo.getInstance(this).unhighlightElements();
      }
    },
    /**
     * IP 구간 툴팁 내용
     * @author KangBongHun(toto1323)
     * @return {[type]} [description]
     */
    getTooltipIpRange() {
      let tooltip = '<ul class="ip-range-tooltip">';
      forEach(this.getIpRanges, (ip, idx) => {
        if (idx !== 0) {
          const li = `<li>${this.getIpRange(ip)}</li>`;
          tooltip += li;
        }
      });
      return `${tooltip}</ul>`;
    },
    /**
     * IP 구간 정보 formatter
     * @author KangBongHun(toto1323)
     * @param  {[type]} ip [description]
     * @return {[type]}    [description]
     */
    getIpRange(ip) {
      let tempText = '';
      if (isEqual(ip.scanType, 'SPOT')) {
        tempText = `[${this.scanTypeFormatter(ip.scanType)}] ${ip.startAddress}`;
      } else if (isEqual(ip.scanType, 'RANGE')) {
        tempText = `[${this.scanTypeFormatter(ip.scanType)}] ${ip.startAddress} - ${ip.endAddress}`;
      } else {
        tempText = `[${this.scanTypeFormatter(ip.scanType)}] ${ip.startAddress} / 서브넷:${ip.endAddress}`;
      }
      return tempText;
    },
    /**
     * 구간 타입 formatter
     * @author KangBongHun(toto1323)
     * @param  {[type]} value [description]
     * @return {[type]}       [description]
     */
    scanTypeFormatter(value) {
      let result = null;
      if (!isNil(value)) {
        if (isEqual(value, 'SPOT')) {
          result = '단일 IP';
        } else if (isEqual(value, 'RANGE')) {
          result = 'IP 구간';
        } else {
          result = '서브넷마스크';
        }
      }
      return result;
    },
    /**
     * 연결정보 수집 모델링이 진행 중인지 확인하고, 진행률을 나타내도록한다.
     * @author KangBongHun(toto1323)
     * @return {[type]} [description]
     */
    checkAutomapProgress() {
      const progressRate = this.getSchedule.progressRate;
      if (progressRate && isNumber(progressRate) && progressRate > 0 && progressRate < 100) {
        const scheduleId = this.getSchedule.scheduleId;
        this.showAutotopologyModelingProgress(scheduleId);
      }
    },
    /**
     * 연결정보 수집 스케줄 변경 완료하면 호출되며, 맵을 새로고침한다.
     * @author KangBongHun(toto1323)
     * @return {[type]} [description]
     */
    onChangeScheduleSetting() {
      const canvas = MapInfo.getInstance(this).getCanvas();
      if (canvas) {
        canvas.refreshMap(this.currentMapId);
        this.init();
      }
    },
    /**
     * 맵 새로고침
     * @author KangBongHun(toto1323)
     * @return {[type]} [description]
     */
    refreshMap() {
      const canvas = MapInfo.getInstance(this).getCanvas();
      if (canvas) {
        canvas.refreshMap(this.currentMapId);
      }
    },
    /**
     * 연결정보 변경 Push 받기 위한 WebSocket
     * @author KangBongHun(toto1323)
     * @return {[type]} [description]
     */
    openWebSocket() {
      // 웹소켓 연결하기전에 정리한다.
      this.closeWebSocket();

      const protocol = window.location.protocol.indexOf('https') > -1 ? 'wss' : 'ws';

      this.webSocket = new WebSocket(`${protocol}://${window.location.host}/update-automap.do`);
      // this.webSocket = new WebSocket(`${protocol}://192.168.143.122:8080/update-automap.do`);

      if (this.webSocket) {
        this.webSocket.onmessage = (e) => {
          try {
            const scheduleIds = JSON.parse(e.data);
            if (scheduleIds.indexOf(this.getSchedule.scheduleId) > -1) {
              this.refreshMap();
              this.$message.success('연결정보 수집이 완료되어 맵을 새로고침 하였습니다.');
              this.init();
            }
          } catch (e2) {
            // ignore
          }
        };
        this.webSocket.onopen = () => {
          console.log('Automap WebSocket opened');
        };
        this.webSocket.onclose = () => {
          console.log('Automap WebSocket closed');
        };
      }
    },
    closeWebSocket() {
      if (this.webSocket) {
        this.webSocket.close();
        this.webSocket = null;
      }
    },
  },
};
</script>

<style lang="scss">
.map-automap-title {
  display: inline-block;
  vertical-align: middle;
  margin-top: -7px;

  span {
    display: inline-block;
    margin-top: 0px;
    margin-left: 0px;
    vertical-align: inherit;
    font-size: 13px;
    font-weight: 400;
    letter-spacing: 0;

    & > span {
      color: #777;
    }

    &:not(:first-child) {
      margin-left: 10px;
    }

    &.map-automap-title-clickable {
      padding: 6px;
      border-radius: 4px;
      pointer-events: none; // 기본은 클릭이벤트 막기

      &.ip,
      &.date {
        cursor: pointer;
        pointer-events: auto;
        &:hover {
          background-color: #f0f0f0;
        }
      }

      &.add {
        color: #1E7EFA;
        cursor: pointer;
        pointer-events: auto;
        & > span {
          color: #1E7EFA;
        }
        &:hover {
          background-color: #f0f0f0;
        }
        &.selected {
          background-color: rgba(30, 126, 250, .85);
          color: #fff;
          & > span {
            color: #fff;
          }
        }
      }

      &.delete {
        color: #F23F5C;
        cursor: pointer;
        pointer-events: auto;
        & > span {
          color: #F23F5C;
        }
        &:hover {
          background-color: #f0f0f0;
        }
        &.selected {
          background-color: rgba(242, 63, 92, .85);
          color: #fff;
          & > span {
            color: #fff;
          }
        }
      }
    }
    &.execute-automap-btn {
      margin-top: 0;
      margin-left: -2px;
    }
    &.ip-count {
      padding: 3px;
      background: #888888;
      border-radius: 4px;
      color: #fff;
      font-size: 10px;
      margin-left: -3px !important;
      margin-bottom: 2px !important;
    }
    &.more {
      height: 20px;
      padding: 0 3px 0 7px;
      border: 1px solid #ddd;
      border-radius: 10px;
      margin-bottom: 2px;
      font-size: 11px;
      color: #333;
      line-height: normal;
      cursor: pointer;

      &:hover {
        background: #f0f0f0;
      }
    }
  }
}
.ip-range-tooltip {
  & > li:not(:last-child) {
    margin-bottom: 3px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/topologymap/components/monitoring/MapAutomapTitle.vue